import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  GppBadOutlined,
  ReportProblemOutlined,
  VpnKeyOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
// Use Redux state instead of local JSON data
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchRiskScenarios } from "../../redux/actions/riskScenariosActionCreators";
import SideList from "./sideList";
import {
  fetchEvaluationList,
  generate_report,
} from "../../redux/actions/evaluationAction";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

export default function RiskScenarios() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [checkedIndices, setCheckedIndices] = useState(new Set());
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const orgaId =
    useSelector((state) => state.organizationProfile.id) ||
    localStorage.getItem("orgaId");

  const riskScenariosData =
    useSelector((state) => state.riskScenarios.risk_scenarios) || [];

  const thisOrgaProfile =
    useSelector((state) => state.organizationProfile.organizationMetricsById) ||
    {};
  const evaluationList = useSelector(
    (state) => state.evaluation?.evaluations ?? []
  );

  const normalizeStatus = (value) => {
    const normalized = String(value ?? "not_started").trim().toLowerCase();
    if (normalized === "in_progress") return "in_progress";
    if (normalized === "not_started" ) {
      return "not_started";
    }
    if (normalized === "failure") {
      return "failed";
    }
    return normalized;
  };

  const riskAssessmentStatus = normalizeStatus(
    evaluationList.find((phase) => phase.phase === "risk_assesment")?.status
  );
  const riskScenariosPhase = evaluationList.find(
    (phase) => phase.phase === "risk_scenarios"
  );
  const riskScenariosStatus = normalizeStatus(riskScenariosPhase?.status);
  const validationEnabled =
    typeof riskScenariosPhase?.validation_status === "boolean"
      ? riskScenariosPhase.validation_status
      : typeof thisOrgaProfile?.validation_status === "boolean"
      ? thisOrgaProfile.validation_status
      : true;
  const shouldHideSelectionCheckbox =
    !validationEnabled && riskScenariosStatus === "completed";
  const canValidateSelection =
    validationEnabled && riskAssessmentStatus === "not_started";

  const selectedScenario = riskScenariosData[selectedIndex];
  const associatedVulnerabilities =
    selectedScenario?.associated_asset?.associated_vulnerabilities;
  const contactFrequency = selectedScenario?.contact_frequency;
  const lossEffects = Array.isArray(selectedScenario?.loss_effects)
    ? selectedScenario.loss_effects.filter(
        (effect) => typeof effect === "string" && effect.trim()
      )
    : selectedScenario?.loss_effects
    ? [selectedScenario.loss_effects]
    : [];
  const scenarioTags = [
    {
      label: "Threat Actor",
      value: selectedScenario?.threat_actor_type,
      icon: GppBadOutlined,
    },
    {
      label: "Threat Method",
      value: selectedScenario?.threat_method,
      icon: ReportProblemOutlined,
    },
    {
      label: "Initial Access Method",
      value: selectedScenario?.initial_access_method,
      icon: VpnKeyOutlined,
    },
  ].filter((tag) => tag.value);
  const scenarioTagPalette = {
    border: "#d9e3e8",
    background: "#ffffff",
    accent: "#c54747",
    iconBackground: "#fff1f1",
    title: "#5f6f82",
    value: "#1c2733",
    shadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
  };
  const hasContactFrequencyRange =
    contactFrequency?.min !== undefined && contactFrequency?.max !== undefined;
  const likelihoodOfOccurrence = hasContactFrequencyRange
    ? (Number(contactFrequency.min) + Number(contactFrequency.max)) / 2
    : null;
  const formattedLikelihoodOfOccurrence =
    likelihoodOfOccurrence !== null && !Number.isNaN(likelihoodOfOccurrence)
      ? likelihoodOfOccurrence.toFixed(2)
      : null;

  const formatCommaSeparatedValues = (value) => {
    if (Array.isArray(value)) {
      return value
        .filter((item) => typeof item === "string" && item.trim())
        .join(", ");
    }

    return value || "-";
  };

  const handleListItemClick = (index) => {
    setSelectedIndex(index);
  };

  const getSelectedScenarioIds = () =>
    Array.from(checkedIndices)
      .sort((a, b) => a - b)
      .map((idx) => riskScenariosData[idx]?.id)
      .filter(Boolean);

  const closeChoiceDialog = () => {
    setChoiceDialogOpen(false);
  };

  const handleSelectionChoice = (userChoice) => {
    const selectedScenarioIds = getSelectedScenarioIds();
    setChoiceDialogOpen(false);
    dispatch(
      generate_report(orgaId, {
        scenario_ids: selectedScenarioIds,
        user_choice: userChoice,
      })
    ).then(async (res) => {
      if (res?.status === 200) {
        await dispatch(fetchEvaluationList(orgaId));
        history.push("/evaluation");
      }
    });
  };

  const handleToggleCheck = (index) => {
    if (!validationEnabled) return;
    setCheckedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  useEffect(() => {
    // Dispatch an action to fetch risk scenarios when the component mounts
    dispatch(fetchRiskScenarios(orgaId));
    dispatch(fetchEvaluationList(orgaId));
  }, [dispatch, orgaId]);

  return (
    <Box sx={{ pl: 3, pr: 3 }}>
      {validationEnabled && (
        <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            {t("select_scenario")}
          </Typography>

          <Button
            variant="contained"
            sx={{
              alignItems: "end",
              ml: "auto",
              borderRadius: 999,
              textTransform: "none",
              px: 2.25,
              backgroundColor: theme.palette.success.main,
              "&:hover": {
                backgroundColor: theme.palette.success.dark,
              },
            }}
            color="success"
            disabled={
              checkedIndices.size === 0 ||
              checkedIndices.size > 5 ||
              !canValidateSelection ||
              thisOrgaProfile.generate_report_status === "in_progress"
            }
            onClick={() => setChoiceDialogOpen(true)}
          >
            Validate selection
          </Button>
        </Box>
      )}
      {checkedIndices.size > 5 && (
        <Box sx={{ px: 3 }}>
          <Typography variant="body2" color="error">
            You can select up to 5 scenarios.
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <SideList
            riskScenariosData={riskScenariosData}
            selectedIndex={selectedIndex}
            onSelect={handleListItemClick}
            checkedIndices={checkedIndices}
            onToggleCheck={handleToggleCheck}
            allowSelection={validationEnabled}
            showSelectionCheckbox={!shouldHideSelectionCheckbox}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              height: { xs: "auto", md: "calc(100vh - 200px)" },
              overflow: "auto",
            }}
          >
            {selectedScenario && (
              <Box>
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        lineHeight: { xs: 1.25, sm: 1.3 },
                        display: "-webkit-box",
                        WebkitLineClamp: { xs: 3, sm: 2 },
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedScenario.title}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {selectedScenario.description}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 1.25,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "#5f6f82",
                          letterSpacing: 0.2,
                        }}
                      >
                        Loss Effects
                      </Typography>
                    </Box>
                    {lossEffects.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {lossEffects.map((effect) => (
                          <Chip
                            key={effect}
                            label={effect}
                            sx={{
                              height: "auto",
                              borderRadius: "10px",
                              border: "1px solid #d9e3e8",
                              backgroundColor: "#ffffff",
                              color: "#1c2733",
                              "& .MuiChip-label": {
                                display: "block",
                                whiteSpace: "normal",
                                padding: "8px 12px",
                                fontWeight: 600,
                                lineHeight: 1.4,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        -
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.25,
                      }}
                    >
                      {scenarioTags.map((tag) => {
                        const TagIcon = tag.icon;

                        return (
                          <Box
                            key={tag.label}
                            sx={{
                              minWidth: 0,
                              p: 1.35,
                              borderRadius: 2,
                              border: `1px solid ${scenarioTagPalette.border}`,
                              backgroundColor: scenarioTagPalette.background,
                              boxShadow: scenarioTagPalette.shadow,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor:
                                    scenarioTagPalette.iconBackground,
                                  flexShrink: 0,
                                }}
                              >
                                <TagIcon
                                  sx={{
                                    fontSize: 17,
                                    color: scenarioTagPalette.accent,
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: scenarioTagPalette.title,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.4,
                                }}
                              >
                                {tag.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: scenarioTagPalette.value,
                                fontWeight: 600,
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                              }}
                            >
                              {tag.value}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Associated Asset
                      </Typography>

                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              color: "text.secondary",
                            }}
                          >
                            Asset Name
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {selectedScenario.associated_asset?.asset_name || "-"}
                          </Typography>

                          {associatedVulnerabilities && (
                            <>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 2,
                                  mt: 2,
                                  color: "text.secondary",
                                }}
                              >
                                Associated Vulnerabilities
                              </Typography>
                              {Array.isArray(associatedVulnerabilities) &&
                              associatedVulnerabilities.some(
                                (vuln) =>
                                  vuln &&
                                  typeof vuln === "object" &&
                                  !Array.isArray(vuln)
                              ) ? (
                                <Box
                                  sx={{
                                    maxHeight: 300,
                                    overflowY: "auto",
                                    pr: 1,
                                  }}
                                >
                                  {associatedVulnerabilities
                                    .filter(
                                      (vuln) =>
                                        vuln &&
                                        typeof vuln === "object" &&
                                        !Array.isArray(vuln)
                                    )
                                    .map((vuln, index) => (
                                      <Card
                                        key={index}
                                        variant="outlined"
                                        sx={{
                                          mb: 2,
                                          backgroundColor: "#f9f9f9",
                                        }}
                                      >
                                        <CardContent>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 600, mb: 1 }}
                                          >
                                            {vuln.name}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary" }}
                                          >
                                            {vuln.description}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                </Box>
                              ) : (
                                <Typography variant="body2">
                                  {formatCommaSeparatedValues(associatedVulnerabilities)}
                                </Typography>
                              )}
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {contactFrequency && (
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Threat Landscape
                        </Typography>

                        <Card variant="outlined">
                          <CardContent>
                            {formattedLikelihoodOfOccurrence && (
                              <Box
                                sx={{
                                  mb: 3,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "text.secondary",
                                  }}
                                >
                                  Likelyhood of occurance
                                </Typography>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: 0.25,
                                    px: 1.5,
                                    py: 1.1,
                                    minWidth: 170,
                                    borderRadius: 2.5,
                                    border: "1px solid #d9e3e8",
                                    background: "#ffffff"
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                      color: "#5f6f82",
                                    }}
                                  >
                                    Events / Year
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: 700,
                                      lineHeight: 1,
                                      color: "#1c2733",
                                    }}
                                  >
                                    {formattedLikelihoodOfOccurrence}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {contactFrequency.justification && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    lineHeight: 1.8,
                                    color: "text.secondary",
                                  }}
                                >
                                  {contactFrequency.justification}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>      <Dialog open={choiceDialogOpen} onClose={closeChoiceDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Select Your Report Generation Method
          <IconButton
            aria-label="close"
            onClick={closeChoiceDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: "text.primary", mb: 0.5 }}>
            You have selected your risk scenarios.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.primary", mb: 1 }}>
            Please choose how you want to proceed:
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.75,
              listStyleType: "disc",
              listStylePosition: "outside",
              color: "text.primary",
            }}
          >
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body1">
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Generate Quiz:
                </Box>{" "}
                Cytailor will generate a Quiz based on your selected scenarios. Your answers will be used to generate your Risk Report.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 0 }}>
              <Typography variant="body1">
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Cytailor Assumptions:
                </Box>{" "}
                Cytailor will automatically create default assumptions based on your selected scenarios. You will review and validate these assumptions before the report is generated.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => handleSelectionChoice("quiz")}
            disabled="true"
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2.4,
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              "&:hover": { backgroundColor: theme.palette.secondary.dark }
            }}
          >
            Generate Quiz
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSelectionChoice("cytailor_assumptions")}
            sx={{
              // textTransform: "none",
              // borderRadius: 999,
              // px: 2.4,
              // borderColor: alpha(theme.palette.primary.main, 0.35),
              // color: theme.palette.primary.main,
              // "&:hover": {
              //   borderColor: theme.palette.primary.main,
              //   backgroundColor: alpha(theme.palette.primary.main, 0.08),
              // },
              textTransform: "none",
              borderRadius: 999,
              px: 2.4,
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              "&:hover": { backgroundColor: theme.palette.secondary.dark }
            }}
          >
            Cytailor Assumptions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


